import { Editor } from '../core/Editor';

export type CanvasUnit = 'px' | 'in';

export class NewCanvasModal {
  private editor: Editor;
  private container: HTMLDivElement | null = null;
  private activeUnit: CanvasUnit = 'px';
  private currentDpi: number = 300;

  constructor(editor: Editor) {
    this.editor = editor;
  }

  public open() {
    this.close();

    const overlay = document.createElement('div');
    overlay.className = 'modal-backdrop';
    overlay.id = 'new-canvas-modal';

    overlay.innerHTML = `
      <div class="modal-card new-canvas-card">
        <div class="modal-header">
          <div class="modal-title-group">
            <div class="modal-icon">📄</div>
            <div>
              <h3>New Document</h3>
              <p class="modal-subtitle">Choose canvas size in pixels (px) or inches (in)</p>
            </div>
          </div>
          <button class="modal-close-btn" id="close-new-canvas">&times;</button>
        </div>

        <div class="modal-body">
          <!-- Unit Selector Tabs -->
          <div class="unit-tabs-row">
            <span class="unit-tabs-label">Measurement Unit:</span>
            <div class="unit-tabs-group">
              <button type="button" class="unit-tab-btn active" id="unit-tab-px" data-unit="px">
                <span>Pixels (px)</span>
              </button>
              <button type="button" class="unit-tab-btn" id="unit-tab-in" data-unit="in">
                <span>Inches (in)</span>
              </button>
            </div>
          </div>

          <!-- Presets Grid -->
          <div class="presets-section-title">
            <span id="presets-category-title">Popular Presets</span>
            <span class="presets-hint" id="presets-hint">Click any preset to apply</span>
          </div>
          <div class="preset-grid" id="presets-grid">
            <!-- Rendered dynamically based on unit -->
          </div>

          <!-- Custom Dimensions Form -->
          <div class="dimensions-form-box">
            <div class="custom-dims-row">
              <div class="dim-field">
                <label id="w-label">Width (px)</label>
                <input type="number" id="new-w" value="1920" min="1" max="12000" step="1" class="styled-input" />
              </div>

              <!-- Orientation / Swap button -->
              <div class="dim-field-swap">
                <button type="button" class="swap-dim-btn" id="btn-swap-dims" title="Swap Width and Height (Landscape / Portrait)">
                  ⇄
                </button>
              </div>

              <div class="dim-field">
                <label id="h-label">Height (px)</label>
                <input type="number" id="new-h" value="1080" min="1" max="12000" step="1" class="styled-input" />
              </div>

              <!-- Resolution (DPI) field - active for inches or high res -->
              <div class="dim-field" id="dpi-container" style="display: none;">
                <label>Resolution (DPI / PPI)</label>
                <select id="new-dpi" class="styled-select">
                  <option value="300" selected>300 DPI (High Res Print)</option>
                  <option value="150">150 DPI (Draft Print)</option>
                  <option value="72">72 DPI (Standard Web/Screen)</option>
                </select>
              </div>
            </div>

            <!-- Background & Live Conversion Readout -->
            <div class="bg-and-readout-row">
              <div class="dim-field bg-field">
                <label>Background Fill</label>
                <select id="new-bg" class="styled-select">
                  <option value="transparent">Transparent (Checkerboard)</option>
                  <option value="#ffffff" selected>White (#FFFFFF)</option>
                  <option value="#000000">Pure Black (#000000)</option>
                  <option value="#0f172a">Dark Slate (#0F172A)</option>
                  <option value="custom">Custom Color...</option>
                </select>
                <input type="color" id="custom-color-picker" value="#ffffff" class="styled-color-inline" style="display: none;" />
              </div>

              <!-- Real-time Pixel Summary Badge -->
              <div class="live-calc-badge" id="live-calc-badge">
                <div class="calc-label">Total Canvas Size</div>
                <div class="calc-val" id="calc-dimensions-text">1920 × 1080 px</div>
                <div class="calc-sub" id="calc-sub-text">Standard Screen Full HD</div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-actions">
          <button class="btn btn-secondary" id="cancel-new-canvas">Cancel</button>
          <button class="btn btn-primary" id="create-new-canvas">Create Canvas</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    this.container = overlay;

    this.attachModalEvents(overlay);
    this.renderPresets();
    this.updateLiveCalculations();
  }

  private attachModalEvents(overlay: HTMLElement) {
    overlay.querySelector('#close-new-canvas')?.addEventListener('click', () => this.close());
    overlay.querySelector('#cancel-new-canvas')?.addEventListener('click', () => this.close());

    const wInput = overlay.querySelector('#new-w') as HTMLInputElement;
    const hInput = overlay.querySelector('#new-h') as HTMLInputElement;
    const dpiSelect = overlay.querySelector('#new-dpi') as HTMLSelectElement;
    const bgSelect = overlay.querySelector('#new-bg') as HTMLSelectElement;
    const customColor = overlay.querySelector('#custom-color-picker') as HTMLInputElement;

    // Unit toggle buttons
    const tabPx = overlay.querySelector('#unit-tab-px');
    const tabIn = overlay.querySelector('#unit-tab-in');

    tabPx?.addEventListener('click', () => {
      if (this.activeUnit !== 'px') {
        this.switchUnit('px');
      }
    });

    tabIn?.addEventListener('click', () => {
      if (this.activeUnit !== 'in') {
        this.switchUnit('in');
      }
    });

    // Dimension input changes
    wInput.addEventListener('input', () => this.updateLiveCalculations());
    hInput.addEventListener('input', () => this.updateLiveCalculations());

    dpiSelect.addEventListener('change', () => {
      this.currentDpi = parseInt(dpiSelect.value) || 300;
      this.updateLiveCalculations();
    });

    // Swap dimensions
    overlay.querySelector('#btn-swap-dims')?.addEventListener('click', () => {
      const temp = wInput.value;
      wInput.value = hInput.value;
      hInput.value = temp;
      this.updateLiveCalculations();
    });

    // Background color
    bgSelect.addEventListener('change', () => {
      if (bgSelect.value === 'custom') {
        customColor.style.display = 'inline-block';
        customColor.click();
      } else {
        customColor.style.display = 'none';
      }
    });

    // Create Canvas
    overlay.querySelector('#create-new-canvas')?.addEventListener('click', () => {
      const finalDims = this.calculatePixelDimensions();
      let bg = bgSelect.value;
      if (bg === 'custom') {
        bg = customColor.value || '#ffffff';
      }

      this.editor.initNewCanvas(finalDims.w, finalDims.h, bg);
      this.close();
    });
  }

  private switchUnit(newUnit: CanvasUnit) {
    const wInput = this.container?.querySelector('#new-w') as HTMLInputElement;
    const hInput = this.container?.querySelector('#new-h') as HTMLInputElement;
    const wLabel = this.container?.querySelector('#w-label');
    const hLabel = this.container?.querySelector('#h-label');
    const dpiContainer = this.container?.querySelector('#dpi-container') as HTMLElement;
    const tabPx = this.container?.querySelector('#unit-tab-px');
    const tabIn = this.container?.querySelector('#unit-tab-in');

    const curW = parseFloat(wInput.value) || 0;
    const curH = parseFloat(hInput.value) || 0;
    const dpi = this.currentDpi;

    if (newUnit === 'in') {
      this.activeUnit = 'in';
      tabPx?.classList.remove('active');
      tabIn?.classList.add('active');

      if (wLabel) wLabel.textContent = 'Width (in)';
      if (hLabel) hLabel.textContent = 'Height (in)';
      if (dpiContainer) dpiContainer.style.display = 'flex';

      wInput.step = '0.1';
      hInput.step = '0.1';
      wInput.max = '200';
      hInput.max = '200';

      // Convert px to inches
      const inW = Math.round((curW / dpi) * 10) / 10;
      const inH = Math.round((curH / dpi) * 10) / 10;
      wInput.value = inW > 0 ? inW.toString() : '8.5';
      hInput.value = inH > 0 ? inH.toString() : '11';
    } else {
      this.activeUnit = 'px';
      tabIn?.classList.remove('active');
      tabPx?.classList.add('active');

      if (wLabel) wLabel.textContent = 'Width (px)';
      if (hLabel) hLabel.textContent = 'Height (px)';
      if (dpiContainer) dpiContainer.style.display = 'none';

      wInput.step = '1';
      hInput.step = '1';
      wInput.max = '12000';
      hInput.max = '12000';

      // Convert inches to px
      const pxW = Math.round(curW * dpi);
      const pxH = Math.round(curH * dpi);
      wInput.value = pxW > 0 ? pxW.toString() : '1920';
      hInput.value = pxH > 0 ? pxH.toString() : '1080';
    }

    this.renderPresets();
    this.updateLiveCalculations();
  }

  private renderPresets() {
    const grid = this.container?.querySelector('#presets-grid');
    if (!grid) return;

    if (this.activeUnit === 'in') {
      grid.innerHTML = `
        <button type="button" class="preset-card active" data-w="8.5" data-h="11" data-unit="in" data-dpi="300">
          <span class="p-title">US Letter</span>
          <span class="p-dim">8.5 &times; 11 in</span>
          <span class="p-desc">2550 &times; 3300 px @ 300 DPI</span>
        </button>
        <button type="button" class="preset-card" data-w="4" data-h="6" data-unit="in" data-dpi="300">
          <span class="p-title">Photo 4&times;6</span>
          <span class="p-dim">4 &times; 6 in</span>
          <span class="p-desc">1200 &times; 1800 px @ 300 DPI</span>
        </button>
        <button type="button" class="preset-card" data-w="5" data-h="7" data-unit="in" data-dpi="300">
          <span class="p-title">Photo 5&times;7</span>
          <span class="p-dim">5 &times; 7 in</span>
          <span class="p-desc">1500 &times; 2100 px @ 300 DPI</span>
        </button>
        <button type="button" class="preset-card" data-w="8" data-h="10" data-unit="in" data-dpi="300">
          <span class="p-title">Portrait 8&times;10</span>
          <span class="p-dim">8 &times; 10 in</span>
          <span class="p-desc">2400 &times; 3000 px @ 300 DPI</span>
        </button>
        <button type="button" class="preset-card" data-w="11" data-h="17" data-unit="in" data-dpi="300">
          <span class="p-title">Tabloid / Poster</span>
          <span class="p-dim">11 &times; 17 in</span>
          <span class="p-desc">3300 &times; 5100 px @ 300 DPI</span>
        </button>
        <button type="button" class="preset-card" data-w="12" data-h="12" data-unit="in" data-dpi="300">
          <span class="p-title">Square 12&times;12</span>
          <span class="p-dim">12 &times; 12 in</span>
          <span class="p-desc">3600 &times; 3600 px @ 300 DPI</span>
        </button>
      `;
    } else {
      grid.innerHTML = `
        <button type="button" class="preset-card active" data-w="1920" data-h="1080" data-unit="px">
          <span class="p-title">Full HD</span>
          <span class="p-dim">1920 &times; 1080 px</span>
          <span class="p-desc">Desktop / Banner 16:9</span>
        </button>
        <button type="button" class="preset-card" data-w="1080" data-h="1080" data-unit="px">
          <span class="p-title">Square HD</span>
          <span class="p-dim">1080 &times; 1080 px</span>
          <span class="p-desc">Instagram / Product 1:1</span>
        </button>
        <button type="button" class="preset-card" data-w="1080" data-h="1920" data-unit="px">
          <span class="p-title">Story / Reel</span>
          <span class="p-dim">1080 &times; 1920 px</span>
          <span class="p-desc">Mobile Vertical 9:16</span>
        </button>
        <button type="button" class="preset-card" data-w="1280" data-h="720" data-unit="px">
          <span class="p-title">Thumbnail HD</span>
          <span class="p-dim">1280 &times; 720 px</span>
          <span class="p-desc">YouTube / Video 16:9</span>
        </button>
        <button type="button" class="preset-card" data-w="2560" data-h="1440" data-unit="px">
          <span class="p-title">2K QHD</span>
          <span class="p-dim">2560 &times; 1440 px</span>
          <span class="p-desc">High Resolution Screen</span>
        </button>
        <button type="button" class="preset-card" data-w="3840" data-h="2160" data-unit="px">
          <span class="p-title">4K UHD</span>
          <span class="p-dim">3840 &times; 2160 px</span>
          <span class="p-desc">Ultra High Definition</span>
        </button>
      `;
    }

    const presetCards = grid.querySelectorAll('.preset-card');
    presetCards.forEach(card => {
      card.addEventListener('click', (e) => {
        presetCards.forEach(c => c.classList.remove('active'));
        const target = e.currentTarget as HTMLElement;
        target.classList.add('active');

        const wInput = this.container?.querySelector('#new-w') as HTMLInputElement;
        const hInput = this.container?.querySelector('#new-h') as HTMLInputElement;
        const dpiSelect = this.container?.querySelector('#new-dpi') as HTMLSelectElement;

        if (wInput) wInput.value = target.dataset.w || '1920';
        if (hInput) hInput.value = target.dataset.h || '1080';
        if (target.dataset.dpi && dpiSelect) {
          dpiSelect.value = target.dataset.dpi;
          this.currentDpi = parseInt(target.dataset.dpi);
        }

        this.updateLiveCalculations();
      });
    });
  }

  private calculatePixelDimensions(): { w: number; h: number } {
    const wInput = this.container?.querySelector('#new-w') as HTMLInputElement;
    const hInput = this.container?.querySelector('#new-h') as HTMLInputElement;

    const rawW = parseFloat(wInput?.value || '1920') || 1920;
    const rawH = parseFloat(hInput?.value || '1080') || 1080;

    if (this.activeUnit === 'in') {
      const dpi = this.currentDpi;
      const w = Math.max(10, Math.min(16000, Math.round(rawW * dpi)));
      const h = Math.max(10, Math.min(16000, Math.round(rawH * dpi)));
      return { w, h };
    } else {
      const w = Math.max(10, Math.min(16000, Math.round(rawW)));
      const h = Math.max(10, Math.min(16000, Math.round(rawH)));
      return { w, h };
    }
  }

  private updateLiveCalculations() {
    const dims = this.calculatePixelDimensions();
    const wInput = this.container?.querySelector('#new-w') as HTMLInputElement;
    const hInput = this.container?.querySelector('#new-h') as HTMLInputElement;
    const textElem = this.container?.querySelector('#calc-dimensions-text');
    const subElem = this.container?.querySelector('#calc-sub-text');

    if (textElem) {
      textElem.textContent = `${dims.w.toLocaleString()} × ${dims.h.toLocaleString()} px`;
    }

    if (subElem) {
      if (this.activeUnit === 'in') {
        const inW = parseFloat(wInput?.value || '8.5');
        const inH = parseFloat(hInput?.value || '11');
        subElem.textContent = `${inW}" × ${inH}" at ${this.currentDpi} DPI Print Resolution`;
      } else {
        const megapixels = ((dims.w * dims.h) / 1000000).toFixed(1);
        subElem.textContent = `${megapixels} Megapixels • Screen Resolution`;
      }
    }
  }

  public close() {
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }
}
